/** Per-user "recent colors" strip for the Tier 3 color picker.
 *  Persisted across sessions so the user's recently-used palette
 *  doesn't reset on every tab close — matching every desktop color
 *  picker (Photoshop, Figma, Affinity) and the same UX we already
 *  shipped for the command-palette recents.
 *
 *  Tier 0's `recentColors` was panel-scoped state capped at 8;
 *  Tier 3 promotes it to a persisted, deduped, normalized list
 *  capped at 16 (the spec figure — same width as a 4-row 16-swatch
 *  Photoshop column).
 *
 *  Normalization rules (applied at push time, NOT load time, so a
 *  legacy list is left untouched until the user picks a new color):
 *    - hex is lowercased
 *    - 3-char shorthand expanded to 6-char
 *    - `transparent` is dropped (never useful as a recent)
 *    - exact-string dedupe; previous instance moves to the head
 *
 *  Failures (private mode, quota) degrade silently via the shared
 *  `safeStorage` shim. */

import { createPersistedNamespace, safeStorage } from './user-prefs-storage';
import { parseHex, rgbToHex } from './color-conversion';

const MAX_RECENTS = 16;

const namespace = createPersistedNamespace<string[]>({
  storageKey: 'image-editor:recent-colors:v1',
  defaultValue: [],
  parse: (raw): string[] => {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((x): x is string => typeof x === 'string')
      .filter((s) => parseHex(s) !== null) // discard anything that isn't a valid hex
      .slice(0, MAX_RECENTS);
  },
});

/** Normalize a color string for storage. Returns the canonical
 *  `#rrggbb` form, or `null` if the input can't be stored (bad
 *  hex, or `'transparent'`). */
export function normalizeRecentColor(raw: string): string | null {
  if (!raw || raw === 'transparent') return null;
  const rgb = parseHex(raw);
  return rgb ? rgbToHex(rgb) : null;
}

/** Read the persisted recents list (defensive copy). */
export function loadRecentColors(storage: Storage = safeStorage()): string[] {
  return namespace.load(storage);
}

/** Push a new color to the head of the recents list. Returns the
 *  new list (also persisted). Callers don't need a follow-up
 *  `loadRecentColors` round-trip.
 *
 *  No-op for unstorable inputs (returns the current list unchanged
 *  so consumers can re-render against the result without an extra
 *  null check). */
export function pushRecentColor(raw: string, storage: Storage = safeStorage()): string[] {
  const normalized = normalizeRecentColor(raw);
  if (!normalized) return namespace.load(storage);
  const current = namespace.load(storage);
  const without = current.filter((c) => c !== normalized);
  const next = [normalized, ...without].slice(0, MAX_RECENTS);
  namespace.save(next, storage);
  return next;
}

/** Wipe the persisted list. Wired to a "Clear recents" affordance
 *  inside the picker. */
export function clearRecentColors(storage: Storage = safeStorage()): void {
  namespace.clear(storage);
}

export const RECENT_COLORS_MAX = MAX_RECENTS;
